import { z } from 'zod'
import { GrowthService, EmailCaptureRequest } from '@mc/api'
import { adPartnersTo } from '@mc/track-event'
import { createTracking } from '@mc/tracking-kit'
import type { RegistrationMethod } from '../utils/auth'

const ANALYTICS_PRODUCT_NAME = 'educator'
const BRAND_NAME = 'Educator'

export const educatorPageNames = {
  // LANDING PAGES
  ANNOUNCEMENT: `${BRAND_NAME} announcement page (pre-login)`,
  ENROLLMENT_LANDING: 'educator homepage (pre-login)',
  PROGRAM_DETAILS: 'educator performance page (pre-login)',
  SUBJECT_LANDING: 'educator subject landing page (pre-login)',
  SUBJECT_AI_FOR_LEADERS: 'educator ai_for_leaders subject page (pre-login)',
  SUBJECT_PRODUCT_INNOVATION:
    'educator product_innovation subject page (pre-login)',
  SUBJECT_FINANCE_DATA_INTELLIGENCE:
    'educator finance_and_data_intelligence subject page (pre-login)',
  // AUTH PAGES
  EMAIL_ENTRY: 'step 1 login: insert email or social (pre-login)',
  LOGIN: `step 2 login: enter password (pre-login)`,
  REGISTER: `step 2 login: enter password (pre-login)`,
  RESET_PASSWORD: 'reset your password (pre-login)',
  // Same as LOGIN for tracking funnel purposes
  // ENROLLMENT APPLICATION
  ENROLLMENT_OVERVIEW: 'step 3 application: overview (post-login)',
  ENROLLMENT_APPLICATION: `step 4 application: application form (post-login)`,
  ENROLLMENT_WAITLIST: 'educator waitlist (pre-login)',
  ENROLLMENT_WAITLIST_CONFIRMATION:
    'educator waitlist confirmation (pre-login)',
  // ENROLLMENT STATUS
  ENROLLMENT_STATUS: `educator application status portal (post-login)`,
  ENROLLMENT_STATUS_REJECTED: `educator application status - rejected (post-login)`,
  ENROLLMENT_STATUS_ACCEPTED: `educator application status - accepted (post-login)`,
  ENROLLMENT_STATUS_MISSED_DEADLINE: `educator application status - missed deadline (post-login)`,
  // ENROLLMENT STATUS (after-checkout)
  ENROLLMENT_STATUS_PAID: `educator application status - paid (post-login)`,
  ENROLLMENT_STATUS_ACCESS_SUSPENDED: `educator application status - access suspended (post-login)`,
  // ENROLLMENT CHECKOUT
  ENROLLMENT_CHECKOUT: `educator payment plans (post-login)`,
  ENROLLMENT_CHECKOUT_PAYMENT: `educator payment method (post-login)`,
  ENROLLMENT_UPDATE_PAYMENT_METHOD: `educator update payment method (post-login)`,
  // APP PAGES
  EVENTS: 'educator events page (post-login)',
  EVENT_DETAIL: 'educator event detail page (post-login)',
  EVENT_CHECKOUT: 'educator event checkout page (post-login)',
  AI_LAB_DETAIL: 'ai labs landing page (post-login)',
  INSTRUCTORS: 'educator instructors page (post-login)',
  MODULE_DIALOG: `educator Module Dialog (post-login)`,
  SETTINGS: `educator Settings (post-login)`,
  CONVERSATION: 'educator conversation (post-login)',
  SAVES: 'educator saved page (post-login)',
  RESOURCES: 'educator resource library (post-login)',
  // HOME LANDING & SUBJECT PAGES
  EDUCATOR_HUB: 'educator hub (post-login)',
  EDUCATOR_SUBJECT_LANDING: 'educator subject landing (post-login)',
  ALL_SUBJECTS: 'educator all subjects page (post-login)',
  // ONBOARDING
  COHORT_WELCOME: 'educator cohort welcome page (post-login)',
  // HDYHAU survey step, named to match core/certs' page view.
  REFERRAL_SOURCES: 'Referral Sources',
} as const

const pageSource = z.enum([
  educatorPageNames.ANNOUNCEMENT,
  educatorPageNames.EMAIL_ENTRY,
  educatorPageNames.LOGIN,
  educatorPageNames.REGISTER,
  educatorPageNames.RESET_PASSWORD,
  educatorPageNames.EVENTS,
  educatorPageNames.EVENT_DETAIL,
  educatorPageNames.EVENT_CHECKOUT,
  educatorPageNames.AI_LAB_DETAIL,
  educatorPageNames.INSTRUCTORS,
  educatorPageNames.MODULE_DIALOG,
  educatorPageNames.SETTINGS,
  educatorPageNames.ENROLLMENT_OVERVIEW,
  educatorPageNames.ENROLLMENT_APPLICATION,
  educatorPageNames.ENROLLMENT_STATUS,
  educatorPageNames.ENROLLMENT_STATUS_REJECTED,
  educatorPageNames.ENROLLMENT_STATUS_ACCEPTED,
  educatorPageNames.ENROLLMENT_STATUS_MISSED_DEADLINE,
  educatorPageNames.ENROLLMENT_STATUS_PAID,
  educatorPageNames.ENROLLMENT_STATUS_ACCESS_SUSPENDED,
  educatorPageNames.ENROLLMENT_CHECKOUT,
  educatorPageNames.ENROLLMENT_CHECKOUT_PAYMENT,
  educatorPageNames.ENROLLMENT_UPDATE_PAYMENT_METHOD,
  educatorPageNames.ENROLLMENT_LANDING,
  educatorPageNames.PROGRAM_DETAILS,
  educatorPageNames.SUBJECT_LANDING,
  educatorPageNames.SUBJECT_AI_FOR_LEADERS,
  educatorPageNames.SUBJECT_PRODUCT_INNOVATION,
  educatorPageNames.SUBJECT_FINANCE_DATA_INTELLIGENCE,
  educatorPageNames.ENROLLMENT_WAITLIST,
  educatorPageNames.ENROLLMENT_WAITLIST_CONFIRMATION,
  educatorPageNames.CONVERSATION,
  educatorPageNames.SAVES,
  educatorPageNames.RESOURCES,
  educatorPageNames.EDUCATOR_HUB,
  educatorPageNames.EDUCATOR_SUBJECT_LANDING,
  educatorPageNames.ALL_SUBJECTS,
  educatorPageNames.COHORT_WELCOME,
])

const userTypeSchema = z.object({
  user_type: z.enum(['new user', 'existing user']),
})

const baseSchema = z.object({
  page: pageSource,
  product: z.literal(ANALYTICS_PRODUCT_NAME).default(ANALYTICS_PRODUCT_NAME),
})

const loggedInSchema = z.object({
  ...baseSchema.shape,
  ...userTypeSchema.shape,
})

const goldStandardSchema = z.object({
  product_name: z
    .literal(ANALYTICS_PRODUCT_NAME)
    .default(ANALYTICS_PRODUCT_NAME),
  product: z.literal(ANALYTICS_PRODUCT_NAME).default(ANALYTICS_PRODUCT_NAME),
})

const registrationMethodSchema = z.enum([
  'google',
  'facebook',
  'apple',
  'email',
]) satisfies z.ZodType<RegistrationMethod>

const enrollmentApplicationGoldStandardSchema = z.object({
  em: z.string().email(),
  registration_method: registrationMethodSchema.optional(),
})

// Server-derived; the client echoes the API response rather than recomputing.
// Optional because `analytics` is an untyped passthrough on the contract.
//
// `value` is the applicant's lead value. It is keyed `value`, not `lead_value`,
// because Segment's browser Facebook Pixel destination forwards properties to
// Meta under their own names and Meta only reads `value` as the conversion value
// for value-based bidding. Do not rename it (see utils/leadValueProperties.ts).
const leadValueShape = {
  value: z.number().optional(),
  currency: z.string().optional(),
}

const moduleSchema = z.object({
  subject_slug: z.string(),
  module_slug: z.string(),
  topic_slug: z.string().optional(),
})

const productsSchema = z.array(
  z.object({
    id: z.string(),
    quantity: z.number(),
    price: z.number(),
  }),
)

const orderCompletedProductsSchema = z.array(
  z.object({
    id: z.number(),
    quantity: z.number(),
    price: z.number(),
  }),
)

// Meta-style contents array for Purchase Click. `price` is the LTV when available.
const contentsSchema = z.array(
  z.object({
    id: z.string(),
    quantity: z.number().default(1),
    price: z.number().optional(),
  }),
)

const paymentPlanTypeSchema = z.string()

// The purchase source Commerce stamps on an in-person event order. It is what
// separates a Booth Weekend seat from the program fee everywhere downstream —
// Finance, receipts, and the checkout events below, which deliberately reuse
// the program funnel's event names.
const inPersonPurchaseSourceSchema = z.literal('educator-in-person-event')

// Commerce identity carried by every in-person event checkout event. `page`
// and `event_slug` arrive ambiently from the event page's TrackingAttributes.
const inPersonCommerceShape = {
  purchase_source: inPersonPurchaseSourceSchema,
  content_type: z.literal('product').default('product'),
  product_id: z.number(),
  price: z.number(),
  currency: z.string().default('USD'),
  // Server-derived, from the event's `analytics` block.
  in_person_event_id: z.string().optional(),
  cohort_id: z.string().optional(),
}

// Which card paid for the seat: one already on file, or one entered now.
const inPersonPaymentMethodSchema = z.enum(['saved', 'new'])

// Commerce context on Payment Failure, so an in-person event decline is
// separable from a program-fee decline. Optional: the enrollment checkout
// sends none of it. Exported as a type for the shared checkout form, which
// takes it from whichever surface is charging.
const paymentFailureCommerceSchema = z.object({
  purchase_source: inPersonPurchaseSourceSchema.optional(),
  product_id: z.number().optional(),
  price: z.number().optional(),
  currency: z.string().optional(),
  // No order exists before a charge.
  order_id: z.string().optional(),
})

export type PaymentFailureCommerce = z.infer<
  typeof paymentFailureCommerceSchema
>

// Tracking values for payment_status. The first five are the gold-standard
// values (MCX Jarvis Tracking Spec: Payments); 'refunded' is tracked in
// addition for visibility. InstallmentSchedule#payment_status_display maps
// internal payment states to one of these; any state it does not map to
// yields no payment_status on the event (the value is dropped by safeParse).
export const paymentStatusSchema = z.enum([
  'paid in full',
  'paid all installments',
  'paying installments',
  'grace period',
  'suspended',
  'refunded',
])

export type PaymentStatus = z.infer<typeof paymentStatusSchema>

export const profileModalTabSchema = z.enum([
  'profile',
  'account',
  'achievements',
  'certificate',
])

export type ProfileModalTab = z.infer<typeof profileModalTabSchema>

// Certificate lifecycle as surfaced to analytics. 'generating' is a transient
// client state (poll between claim and issuance) and is intentionally absent —
// the tracking spec only distinguishes locked/review/issued.
export const certificateStatusSchema = z.enum(['locked', 'review', 'issued'])

export type CertificateStatus = z.infer<typeof certificateStatusSchema>

// The presentation an event fired in. For the subject modal's modules section:
// carousel vs list (list shows every topic at once, so impressions aren't
// comparable across views without this). For the Resource Library it carries
// the active modality filter — 'all'/'saved' are the grouped topic views, the
// rest are the flat content-type grid (per the Resource Library tracking spec).
export const modulesViewTypeSchema = z.enum([
  'carousel',
  'list',
  'all',
  'video',
  'image',
  'article',
  'shocking_fact',
  'saved',
])

export type ModulesViewType = z.infer<typeof modulesViewTypeSchema>

/** Off the RESOURCES catalog, a row/tile/click view_type must be the subject
 *  page's 'carousel'/'list'; any library filter there is inherited context that
 *  leaked onto another page's event. Shared by every event that carries both a
 *  `page` and a catalog `view_type` (educatorRowViewed / educatorRowTileViewed /
 *  educatorHubClick), so the guard can't cover one and miss its siblings. */
const refineViewTypeScope = (
  value: { page?: z.infer<typeof pageSource>; view_type?: ModulesViewType },
  ctx: z.RefinementCtx,
) => {
  if (
    value.view_type &&
    value.view_type !== 'carousel' &&
    value.view_type !== 'list' &&
    value.page !== educatorPageNames.RESOURCES
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['view_type'],
      message: `view_type '${value.view_type}' is only valid on the ${educatorPageNames.RESOURCES} page; other pages' events use 'carousel'/'list'`,
    })
  }
}

// A Resource Library resource's modality.
export const resourceTypeSchema = z.enum([
  'video',
  'image',
  'article',
  'shocking_fact',
  'text',
  'topic_summary',
])

export type ResourceType = z.infer<typeof resourceTypeSchema>

// Resource identity carried by Resource Library tile view / click / modal
// events: the resource's modality, its asset (or saved-resource) uuid, the
// message that delivered it, and whether it was locked.
const resourceEventProps = z.object({
  resource_type: resourceTypeSchema.optional(),
  resource_uuid: z.string().optional(),
  message_uuid: z.string().optional(),
  resource_locked: z.boolean().optional(),
})

// The resource-viewer branch shared by educatorModalViewed and
// educatorModalClick — the resource identity plus the catalog context the
// viewer inherits. The click event adds `action` on top of this.
const resourceViewerModalProps = resourceEventProps.extend({
  modal_type: z.literal('resource_viewer'),
  subject_slug: z.string().optional(),
  module_slug: z.string().optional(),
  topic_slug: z.string().optional(),
  view_type: modulesViewTypeSchema.optional(),
})

export const writtenSubmissionFieldSchema = z.enum(['essay', 'linkedin'])

export const writtenSubmissionFieldErrorTypeSchema = z.enum([
  'essay_required',
  'over_word_limit',
  'invalid_characters',
  'linkedin_required',
  'linkedin_invalid',
])

export const writtenSubmissionCloseMethodSchema = z.enum([
  'close_button',
  'backdrop',
  'escape',
])

const writtenSubmissionModalProps = z.object({
  modal_type: z.literal('written submission modal'),
  prompt_variant: z.enum(['control', 'variant_1']),
  has_linkedin_field: z.boolean(),
  open_count: z.number().int().positive(),
})

// JAR-2891: why a chat-bar dictation take failed. 'auth' and
// 'retries-exhausted' come from the Cortex session client, 'mic' from a
// recorder that fails to start, 'denied'/'blocked' from mic permission.
export const dictationErrorReasonSchema = z.enum([
  'auth',
  'retries-exhausted',
  'mic',
  'denied',
  'blocked',
])

export type DictationErrorReason = z.infer<typeof dictationErrorReasonSchema>

export const userInputMethodSchema = z.enum([
  'typed',
  'dictation',
  'quick_reply',
])

export type UserInputMethod = z.infer<typeof userInputMethodSchema>

export const dictationCancelMethodSchema = z.enum([
  'cancel_button',
  'silence_timeout',
  'abandoned',
])

export type DictationCancelMethod = z.infer<typeof dictationCancelMethodSchema>

const commonSchema = baseSchema.and(
  z.discriminatedUnion('page', [
    // AUTH PAGES
    z.object({
      page: pageSource.extract([educatorPageNames.ANNOUNCEMENT]),
    }),
    z.object({
      page: pageSource.extract([educatorPageNames.EMAIL_ENTRY]),
    }),
    z.object({
      page: pageSource.extract([educatorPageNames.LOGIN]),
      ...userTypeSchema.shape,
    }),
    z.object({
      page: pageSource.extract([educatorPageNames.RESET_PASSWORD]),
      ...userTypeSchema.shape,
    }),
    // REGISTER already accounted for by LOGIN page
    z.object({
      page: pageSource.extract([educatorPageNames.ENROLLMENT_LANDING]),
    }),
    // ENROLLMENT APPLICATION PAGES
    z.object({
      page: pageSource.extract([educatorPageNames.ENROLLMENT_OVERVIEW]),
      ...userTypeSchema.shape,
    }),
    z.object({
      page: pageSource.extract([educatorPageNames.ENROLLMENT_APPLICATION]),
      ...userTypeSchema.shape,
    }),
    z.object({
      page: pageSource.extract([educatorPageNames.ENROLLMENT_WAITLIST]),
    }),
    z.object({
      page: pageSource.extract([
        educatorPageNames.ENROLLMENT_WAITLIST_CONFIRMATION,
      ]),
    }),
    // ENROLLMENT STATUS PAGES
    z.object({
      page: pageSource.extract([
        educatorPageNames.ENROLLMENT_STATUS,
        educatorPageNames.ENROLLMENT_STATUS_REJECTED,
        educatorPageNames.ENROLLMENT_STATUS_ACCEPTED,
        educatorPageNames.ENROLLMENT_STATUS_MISSED_DEADLINE,
        educatorPageNames.ENROLLMENT_STATUS_ACCESS_SUSPENDED,
      ]),
    }),
    z.object({
      page: pageSource.extract([educatorPageNames.ENROLLMENT_STATUS_PAID]),
      // Optional: both derive from the learner's installment schedule, and the
      // enrollment-status BE omits them when there is none (e.g. paid in full
      // outside an installment plan). Requiring them made every event on the
      // page fail validation for such learners — and Page Loaded, which drops
      // hard on failure, was never sent at all.
      payment_plan_type: paymentPlanTypeSchema.optional(),
      payment_status: paymentStatusSchema.optional(),
    }),
    z.object({
      page: pageSource.extract([educatorPageNames.ENROLLMENT_CHECKOUT]),
    }),
    z.object({
      page: pageSource.extract([educatorPageNames.ENROLLMENT_CHECKOUT_PAYMENT]),
      payment_plan_type: paymentPlanTypeSchema,
    }),
    z.object({
      page: pageSource.extract([
        educatorPageNames.ENROLLMENT_UPDATE_PAYMENT_METHOD,
      ]),
    }),
    z.object({
      page: pageSource.extract([educatorPageNames.PROGRAM_DETAILS]),
    }),
    z.object({
      page: pageSource.extract([educatorPageNames.SUBJECT_LANDING]),
    }),
    z.object({
      page: pageSource.extract([educatorPageNames.SUBJECT_AI_FOR_LEADERS]),
    }),
    z.object({
      page: pageSource.extract([educatorPageNames.SUBJECT_PRODUCT_INNOVATION]),
    }),
    z.object({
      page: pageSource.extract([
        educatorPageNames.SUBJECT_FINANCE_DATA_INTELLIGENCE,
      ]),
    }),
    z.object({
      page: pageSource.extract([educatorPageNames.EVENTS]),
    }),
    z.object({
      page: pageSource.extract([educatorPageNames.EVENT_DETAIL]),
      // Event identity on every event from this page, matching how
      // AI_LAB_DETAIL keys its clicks/views. Context-provided by the page's
      // TrackingAttributes; optional so a missing value can't hard-fail
      // TrackPageViewed.
      event_slug: z.string().optional(),
    }),
    z.object({
      page: pageSource.extract([educatorPageNames.EVENT_CHECKOUT]),
      // The seat checkout is its own screen; it keys its events to the event
      // the same way the detail page it was reached from does.
      event_slug: z.string().optional(),
    }),
    z.object({
      page: pageSource.extract([educatorPageNames.AI_LAB_DETAIL]),
      // BE-owned identity, forwarded via TrackingAttributes (JAR-2030).
      // Optional so a missing value can't hard-fail TrackPageViewed.
      ai_lab_slug: z.string().optional(),
      // Lab identity on every event from this page: the tracking
      // spec keys clicks/views to the lab via event_slug. Context-provided by
      // the page's TrackingAttributes, declared here so all events carry it.
      event_slug: z.string().optional(),
    }),
    z.object({
      page: pageSource.extract([educatorPageNames.INSTRUCTORS]),
    }),
    z.object({
      page: pageSource.extract([educatorPageNames.MODULE_DIALOG]),
      ...moduleSchema.shape,
    }),
    z.object({
      page: pageSource.extract([educatorPageNames.SETTINGS]),
      ...userTypeSchema.shape,
    }),
    z.object({
      page: pageSource.extract([educatorPageNames.CONVERSATION]),
      ...moduleSchema.shape,
    }),
    z.object({
      page: pageSource.extract([educatorPageNames.SAVES]),
      // The page is subject-scoped; context-provided by its
      // TrackingAttributes so every event carries the selected subject.
      subject_slug: z.string().optional(),
    }),
    z.object({
      page: pageSource.extract([educatorPageNames.RESOURCES]),
      // Page-level context provided by the page's TrackingAttributes, so the
      // landing events (Page Loaded / Educator Viewed) carry it too — the
      // discriminated union strips any prop the branch doesn't declare.
      subject_slug: z.string().optional(),
      module_slug: z.string().optional(),
      view_type: modulesViewTypeSchema.optional(),
    }),
    z.object({
      page: pageSource.extract([educatorPageNames.EDUCATOR_HUB]),
    }),
    z.object({
      page: pageSource.extract([educatorPageNames.ALL_SUBJECTS]),
    }),
    z.object({
      page: pageSource.extract([educatorPageNames.EDUCATOR_SUBJECT_LANDING]),
      subject_slug: z.string().optional(),
      module_slug: z.string().optional(),
      topic_slug: z.string().optional(),
    }),
    z.object({
      page: pageSource.extract([educatorPageNames.COHORT_WELCOME]),
    }),
  ]),
)

const locations = z.enum([
  // POC
  'login-form',
  'sidebar',
  'sidebar - module selector',
  'sidebar - user menu',
  'chat',
  'history',
  // Announcement
  'email form',
  'user info form',
  'hero',
  // The phone-only CTA box under the hero artwork; the band CTA is 'hero'.
  'hero cta box',
  'hero navbar',
  // Enrollment
  'enrollment email form',
  'enrollment navbar',
  'enrollment hero',
  'enrollment overview',
  'enrollment faculty',
  'enrollment program',
  'enrollment experience',
  'enrollment admissions',
  'enrollment faq',
  'enrollment footer',
  'ai labs',
  'signup form',
  'sticky footer',
  'booth checkout card',
  'booth faq',
  'booth founding cohort testimonials',
  'top navigation',
  'sticky nav',
  'curriculum',
  'faq',
  'tos',
  'footer',
  'video application',
  'written submission',
  'step card',
  'step card - application started',
  'step card - video challenge',
  'step card - decision',
  'other subjects',
  'enrollment block',
  'cta band',
  'testimonials',
  'stay informed',
  'events-hero',
  'events-grid',
  'events-calendar',
  'progress modal',
  'sms opt in',
  'approved celebration',
  'webinars',
  'payment plan tabs',
])

const messageRoles = z.enum(['user', 'bot'])

// Spec: MCX Jarvis Tracking Spec - Chat Experience
// https://masterclass-dev.atlassian.net/wiki/spaces/ENG/pages/3585966081
const modalityTypeSchema = z.enum([
  'concept_mapping',
  'sorting',
  'fill_in_blank',
  'matching_pairs',
  'video',
  'transition',
  'flip_cards',
  'longform',
  'multi_image',
  'boogie_badge_name_input',
  'multiple_choice',
  'quiz',
  'topic_summary',
  'subject_progress',
  'voice_roleplay',
])
// Modalities that require the user to perform an exercise to continue.
// These support drag-n-drop, submit, and reset actions.
const exerciseModalitySchema = z.enum([
  'concept_mapping',
  'sorting',
  'fill_in_blank',
  'matching_pairs',
])
const modalityActionSchema = z.enum([
  'drag-n-drop',
  'submit',
  'reset',
  'continue',
  'view',
  'skip',
  'listen',
  'prev',
  'next',
  'done',
  'link-click',
  // Read-along podcast view → article view (inverse of 'listen').
  'back-to-article',
  '90 percent complete',
  'shown',
  'wrong-tap',
  'flip',
  'see_full_summary',
  'preview_open',
  'preview_close',
  'milestone_open',
  'milestone_close',
  'share_linkedin',
  'see_all_subjects',
  'download',
  'start',
  'connected',
  'mute',
  'unmute',
  'end',
  'error',
  'feedback_ready',
])

// Actions valid for the longform (read-along) modality. Exported so the
// React components that surface these CTAs share a single source of truth
// with the runtime Zod enum below — keep them in sync.
const longformActionSchema = modalityActionSchema.extract([
  'listen',
  'prev',
  'next',
  'done',
  'link-click',
  'back-to-article',
])
export type LongformTrackAction = z.infer<typeof longformActionSchema>

// Actions valid for the multi-image modality. Paginated image viewer surfaces
// Prev/Next navigation plus a Done CTA on the last page. Mirrors the longform
// action surface minus 'listen' and 'link-click', which have no analog in the
// image carousel.
const multiImageActionSchema = modalityActionSchema.extract([
  'prev',
  'next',
  'done',
])
export type MultiImageTrackAction = z.infer<typeof multiImageActionSchema>

// Actions valid for the end-of-topic subject progress card. 'view' fires once
// when the card renders on a live turn; 'continue' when the learner advances
// to a lesson from the up-next reveal (alongside the transition 'continue'
// the card it replaces has always fired).
const subjectProgressActionSchema = modalityActionSchema.extract([
  'view',
  'preview_open',
  'preview_close',
  'continue',
  'milestone_open',
  'milestone_close',
  'share_linkedin',
  'see_all_subjects',
  'download',
])
export type SubjectProgressTrackAction = z.infer<
  typeof subjectProgressActionSchema
>

// Voice roleplay: the session starts by itself once the board's
// entrance ends, so there is no start click. Abandon is `end` with
// end_reason 'abandoned', not a separate action.
export const voiceRoleplayEndReasons = [
  'completed',
  'ended_by_ai',
  'abandoned',
] as const
export const voiceRoleplayErrorReasons = [
  'denied',
  'session_create_failed',
  'disconnected',
  'feedback_failed',
] as const

const modalityInteractionBase = {
  conversation_uuid: z.string().optional(),
  modality_uuid: z.string(),
  // The bot message UUID (messages.uuid) that owns this modality. Stable per
  // turn and joinable to backend records, unlike modality_uuid (which derives
  // from regenerated content-blob ids). Optional because DialogMessage.uuid is
  // itself optional in the API type.
  message_uuid: z.string().optional(),
  // The plan activity + activity the turn belongs to, sourced from the stream
  // finish event's analytics (MessageFinishEvent.analytics.{plan_activity_id,
  // activity_id}). Lets a modality interaction be grouped by the learning
  // activity that produced it. Optional: only present on live-streamed turns
  // that have an attempt (the finish event carries them); absent on replayed
  // history and on turns without an attempt (e.g. onboarding).
  plan_activity_id: z.string().optional(),
  activity_id: z.string().optional(),
}

// Inline-UI placement (JAR-3636). `ui_type` is the bare word from the marker
// grammar, not an enum: the unmountable event exists to surface types the
// client has not registered, so the client's list cannot bound it.
const inlineUiPlacementBase = {
  ui_type: z.string().regex(/^[a-z][a-z0-9_]*$/),
  message_uuid: z.string(),
  plan_activity_id: z.string().optional(),
  activity_id: z.string().optional(),
}
const inlineUiPlacementSchema = z.object(inlineUiPlacementBase)
export const inlineUiPositionSchema = z.enum(['final', 'interior'])
const inlineUiMountedSchema = inlineUiPlacementSchema.extend({
  // Where the directive sits in the body: last block, or prose follows it.
  ui_position: inlineUiPositionSchema.optional(),
})
export const inlineUiUnmountableReasonSchema = z.enum([
  // The type is not in the client's inline registry (cortex/client drift).
  'unknown_type',
  // Known type, but the message carries no matching content field.
  'content_missing',
  // Known type and field, but the stacked first-match chain picks another.
  'modality_mismatch',
])

const liveLessonAnalyticsExtras = {
  step: z.string().optional(),
  score: z.string().optional(),
  total: z.string().optional(),
  passed: z.string().optional(),
  attempt_number: z.coerce.number().optional(),
  question_number: z.string().optional(),
  question_count: z.string().optional(),
  passing_score: z.string().optional(),
  video_duration: z.string().optional(),
  attended_live: z.string().optional(),
  content_type: z.string().optional(),
}

// Plain z.union (not z.discriminatedUnion) because 'continue' is a valid
// action for both video and transition modalities — Zod's discriminated
// union requires each discriminator value to appear in only one branch.
const modalityInteractionSchema = z.union([
  // Drag attempt on an exercise modality — fires on BOTH correct + incorrect drops.
  z.object({
    ...modalityInteractionBase,
    activity_type: exerciseModalitySchema,
    action: modalityActionSchema.extract(['drag-n-drop']),
    concept: z.string(),
    user_match: z.string(),
    correct_match: z.string().optional(), // distractor words in fill-in-blank have no correct match
  }),
  // Exercise CTAs — no drag details.
  z.object({
    ...modalityInteractionBase,
    activity_type: exerciseModalitySchema,
    action: modalityActionSchema.extract(['submit', 'reset']),
  }),
  // Video: 'continue' on natural finish, 'skip' when the user skips ahead.
  z.object({
    ...modalityInteractionBase,
    activity_type: modalityTypeSchema.extract(['video']),
    action: modalityActionSchema.extract(['continue', 'skip']),
  }),
  // Transition modality (Next Topic Card): 'continue' CTA.
  z.object({
    ...modalityInteractionBase,
    activity_type: modalityTypeSchema.extract(['transition']),
    action: modalityActionSchema.extract(['continue']),
  }),
  // Flip cards: 'done' when the Done CTA completes the modality; otherwise
  // a card-level identifier (term or id) when the user reveals a card's back
  // face. Per-card identifier lets BI distinguish which card was flipped
  // (JAR-2001).
  z.object({
    ...modalityInteractionBase,
    activity_type: modalityTypeSchema.extract(['flip_cards']),
    action: z.union([z.literal('done'), z.string().min(1)]),
  }),
  // Longform / read-along: CTA-driven ('listen', 'prev', 'next', 'done',
  // 'link-click'). 'done' fires when the Done CTA completes the modality;
  // 'link-click' fires when the user clicks an embedded link in the article.
  z.object({
    ...modalityInteractionBase,
    activity_type: modalityTypeSchema.extract(['longform']),
    action: longformActionSchema,
  }),
  // Multi-image: paginated image viewer. 'prev'/'next' on navigation, 'done'
  // when the user completes the carousel from the final page.
  z.object({
    ...modalityInteractionBase,
    activity_type: modalityTypeSchema.extract(['multi_image']),
    action: multiImageActionSchema,
  }),
  z.object({
    ...modalityInteractionBase,
    activity_type: modalityTypeSchema.extract(['quiz']),
    action: modalityActionSchema.extract(['shown']),
  }),
  z.object({
    ...modalityInteractionBase,
    activity_type: modalityTypeSchema.extract(['quiz']),
    action: modalityActionSchema.extract(['wrong-tap']),
    user_match: z.string(),
  }),
  z.object({
    ...modalityInteractionBase,
    activity_type: modalityTypeSchema.extract(['quiz']),
    action: modalityActionSchema.extract(['submit']),
    wrong_attempt_count: z.number().int().nonnegative(),
  }),
  // End-of-topic summary (JAR-3539): the only interaction is opening the
  // full summary via the Open pill; the artifact card itself is not a control.
  z.object({
    ...modalityInteractionBase,
    activity_type: modalityTypeSchema.extract(['topic_summary']),
    action: modalityActionSchema.extract(['see_full_summary']),
  }),
  // End-of-topic subject progress card (JAR-3145). Progress values are the
  // subject's whole-percent progress after / before this topic; with no
  // snapshot from when the topic opened, before equals after.
  // `trigger` says what opened a preview ('cta' = a What's next button, the
  // card's own or the chat bar's chip; 'grid' = a lesson cell) or the
  // milestone ('auto' on completion, 'row' = mini row).
  z.object({
    ...modalityInteractionBase,
    activity_type: modalityTypeSchema.extract(['subject_progress']),
    action: subjectProgressActionSchema,
    subject_progress: z.number().int().min(0).max(100),
    subject_progress_before: z.number().int().min(0).max(100),
    // The lesson a preview/continue concerns (the common `topic_slug` is the
    // lesson just finished).
    target_topic_slug: z.string().optional(),
    is_next_topic: z.boolean().optional(),
    trigger: z.enum(['cta', 'grid', 'auto', 'row']).optional(),
  }),
  // Voice roleplay. `conversation_id` is the Parley conversation, apart from
  // `conversation_uuid` (the Educator dialog). `duration_ms` runs from
  // `connected` to `end`; `analysis_latency_ms` from `end` to `feedback_ready`.
  z.object({
    ...modalityInteractionBase,
    activity_type: modalityTypeSchema.extract(['voice_roleplay']),
    action: modalityActionSchema.extract(['shown', 'start']),
  }),
  z.object({
    ...modalityInteractionBase,
    activity_type: modalityTypeSchema.extract(['voice_roleplay']),
    action: modalityActionSchema.extract(['connected', 'mute', 'unmute']),
    conversation_id: z.string(),
  }),
  z.object({
    ...modalityInteractionBase,
    activity_type: modalityTypeSchema.extract(['voice_roleplay']),
    action: modalityActionSchema.extract(['end']),
    conversation_id: z.string().optional(),
    end_reason: z.enum(voiceRoleplayEndReasons),
    duration_ms: z.number().int().nonnegative(),
    has_user_spoken: z.boolean(),
  }),
  z.object({
    ...modalityInteractionBase,
    activity_type: modalityTypeSchema.extract(['voice_roleplay']),
    action: modalityActionSchema.extract(['error']),
    conversation_id: z.string().optional(),
    error_reason: z.enum(voiceRoleplayErrorReasons),
  }),
  z.object({
    ...modalityInteractionBase,
    activity_type: modalityTypeSchema.extract(['voice_roleplay']),
    action: modalityActionSchema.extract(['feedback_ready']),
    conversation_id: z.string().optional(),
    analysis_latency_ms: z.number().int().nonnegative(),
  }),
  // Boogie badge name input: CTA action ('continue') when the user confirms
  // first/last name. Spec: MCX (Jarvis) Tracking Spec - Onboarding Experience
  // https://masterclass-dev.atlassian.net/wiki/spaces/ENG/pages/3624894509
  // Section: Profile Badge (Boogie ID).
  z.object({
    ...modalityInteractionBase,
    activity_type: modalityTypeSchema.extract(['boogie_badge_name_input']),
    action: modalityActionSchema.extract(['continue']),
  }),
  z.object({
    ...liveLessonAnalyticsExtras,
    activity_type: modalityTypeSchema.extract(['video']),
    action: modalityActionSchema.extract(['90 percent complete']),
  }),
  z.object({
    ...liveLessonAnalyticsExtras,
    activity_type: modalityTypeSchema.extract(['multiple_choice']),
    action: z.literal('select'),
    concept: z.string(),
    user_match: z.string(),
  }),
  z.object({
    ...liveLessonAnalyticsExtras,
    activity_type: modalityTypeSchema.extract(['multiple_choice']),
    action: z.string().regex(/^quiz (passed|failed) \d+ out of \d+$/),
  }),
])

// AI Labs submission events (JAR-2532).
const uploadMethodSchema = z.enum(['drag_drop', 'file_picker'])

// Integration settings for Gold Standard events (sent to paid media vendors)
export const GOLD_STANDARD_INTEGRATIONS = {
  All: true,
  Amplitude: true,
  'Bing Ads': false,
  'Facebook Pixel': true,
  'Google Ad Words': true,
  'Google Tag Manager': true,
  Iterable: true,
  'Iterable (Actions)': true,
  Hotjar: false,
  ShareASale: false,
  TikTok: false,
}

// Integration settings for non-Gold Standard events (analytics only)
export const NON_GOLD_STANDARD_INTEGRATIONS = {
  ...adPartnersTo(false),
  Hotjar: false,
}

const LCM_INTEGRATIONS = {
  ...NON_GOLD_STANDARD_INTEGRATIONS,
  Iterable: true,
  'Iterable (Actions)': true,
}

// Exported for schema-level regression tests (e.g. asserting an event strips
// properties that leak in from the surrounding TrackingAttributes context).
export const educatorSchema = {
  // ========================================
  // NON-GOLD STANDARD EVENTS (Analytics Only)
  // ========================================
  educatorNavigationClicked: {
    eventName: 'Navigation Clicked',
    schema: commonSchema.and(
      z.object({
        action: z.string(),
        level: z.literal('primary'),
        location: z.literal('utility-navigation'),
        page_source: pageSource,
        product: z.literal(ANALYTICS_PRODUCT_NAME),
      }),
    ),
    options: { integrations: NON_GOLD_STANDARD_INTEGRATIONS },
  },
  educatorClick: {
    eventName: `${BRAND_NAME} Sign Click`,
    schema: commonSchema.and(
      z.object({
        action: z.string(),
        location: locations,
        selected_module_slug: z.string().optional(),
      }),
    ),
    options: { integrations: NON_GOLD_STANDARD_INTEGRATIONS },
  },
  educatorSignClick: {
    eventName: `${BRAND_NAME} Sign Click`,
    schema: commonSchema.and(
      z.object({
        action: z.string(),
        location: locations.optional(),
        payment_plan_type: z.string().optional(),
      }),
    ),
    options: { integrations: NON_GOLD_STANDARD_INTEGRATIONS },
  },
  educatorFormSubmitted: {
    eventName: `${BRAND_NAME} Form Submitted`,
    schema: z.object({
      ...loggedInSchema.shape,
      page: z.literal(educatorPageNames.ENROLLMENT_APPLICATION),
      form_type: z.string(),
      form_id: z.string(),
      question: z.string(),
      answers: z.string(),
    }),
    options: { integrations: NON_GOLD_STANDARD_INTEGRATIONS },
  },
  educatorSignViewed: {
    eventName: `${BRAND_NAME} Sign Viewed`,
    schema: commonSchema.and(
      z.object({
        name: z.string(),
        // Set by component-level impressions on the checkout screen, where the
        // plan on show is what makes the impression worth counting.
        payment_plan_type: paymentPlanTypeSchema.optional(),
      }),
    ),
    options: { integrations: NON_GOLD_STANDARD_INTEGRATIONS },
  },
  educatorRowViewed: {
    eventName: `${BRAND_NAME} Row Viewed`,
    // Only the resource library (page RESOURCES) sets a catalog filter as
    // view_type; every other page's rows use 'carousel'/'list' or none. Reject
    // a library filter elsewhere so a page-context leak onto a row view — e.g. a
    // subject modal opened from the library — fails safeParse instead of passing
    // clean now that modulesViewTypeSchema carries the filter values.
    schema: commonSchema
      .and(
        z.object({
          row_name: z.string(),
          row_position_index: z.number(),
          subject_slug: z.string().optional(),
          module_slug: z.string().optional(),
          topic_slug: z.string().optional(),
          event_slug: z.string().optional(),
          view_type: modulesViewTypeSchema.optional(),
        }),
      )
      .superRefine(refineViewTypeScope),
    options: { integrations: NON_GOLD_STANDARD_INTEGRATIONS },
  },
  // row identity comes from TrackingAttributes, so it joins with educatorRowViewed.
  educatorRowScrolled: {
    eventName: `${BRAND_NAME} Row Scrolled`,
    schema: commonSchema.and(
      z.object({
        row_name: z.string(),
        row_position_index: z.number(),
        direction: z.enum(['left', 'right']),
        auto_scroll: z.boolean(),
      }),
    ),
    options: { integrations: NON_GOLD_STANDARD_INTEGRATIONS },
  },
  educatorPastMessagesScrolled: {
    eventName: `${BRAND_NAME} Past Messages Scrolled`,
    schema: commonSchema.and(
      z.object({
        location: z.literal('history'),
      }),
    ),
    options: { integrations: NON_GOLD_STANDARD_INTEGRATIONS },
  },
  educatorInteractionStarted: {
    eventName: `${BRAND_NAME} Interaction Started`,
    schema: commonSchema.and(
      z.object({
        interaction_id: z.string().uuid(),
        // Chat transport for the turn ('legacy' persona relay | 'direct'
        // cortex).
        transport: z.enum(['legacy', 'direct']),
      }),
    ),
    options: { integrations: NON_GOLD_STANDARD_INTEGRATIONS },
  },
  educatorInteractionFirstContent: {
    eventName: `${BRAND_NAME} Interaction First Content`,
    schema: commonSchema.and(
      z.object({
        interaction_id: z.string().uuid(),
        transport: z.enum(['legacy', 'direct']),
      }),
    ),
    options: { integrations: NON_GOLD_STANDARD_INTEGRATIONS },
  },
  educatorInteraction: {
    eventName: `${BRAND_NAME} Interaction`,
    schema: commonSchema.and(
      z.object({
        conversation_uuid: z.string().optional(),
        message_uuid: z.string(),
        role: messageRoles,
        interaction_id: z.string().uuid(),
        // How the user composed the message (user role only): free-typed,
        // via a dictation take, or a quick-reply pill. Lets data compare
        // send rates / message length for dictation vs typed sends.
        input_method: userInputMethodSchema.optional(),
      }),
    ),
    options: { integrations: NON_GOLD_STANDARD_INTEGRATIONS },
  },
  // Fires alongside the Sprig `Educator Topic Completed` event so we have a
  // BI-side counterpart for verifying survey delivery. Same trigger
  // (`next_topic_link` SSE arrival) and same slug payload — the CONVERSATION
  // page in `commonSchema` already carries `subject_slug`, `module_slug`,
  // and `topic_slug` via `moduleSchema`.
  educatorTopicCompleted: {
    eventName: `${BRAND_NAME} Topic Completed`,
    schema: commonSchema,
    options: { integrations: NON_GOLD_STANDARD_INTEGRATIONS },
  },
  // Thumbs up/down click on the rating toast (JAR-2739). The
  // data-eng spec names this `educator_rating_submitted` — that's the
  // Amplitude-side representation; Segment receives the Title Case name per
  // this file's convention. `feedback_type` scopes the event so future
  // feedback surfaces can reuse it. `topic_id`/`course_id` carry this app's
  // canonical content identifiers (slugs). The spec's `user_id` and
  // `timestamp` ride on the Segment envelope (from identify / sentAt) rather
  // than being duplicated as custom properties.
  educatorRatingSubmitted: {
    eventName: `${BRAND_NAME} Rating Submitted`,
    schema: commonSchema.and(
      z.object({
        rating: z.enum(['up', 'down']),
        feedback_type: z.enum(['topic_rating']),
        topic_id: z.string(),
        // Display number of the topic within its module; omitted when the
        // topic list hasn't loaded or the topic carries no number.
        topic_number: z.number().optional(),
        course_id: z.string(),
      }),
    ),
    options: { integrations: NON_GOLD_STANDARD_INTEGRATIONS },
  },
  // Chat-bar dictation telemetry (epic JAR-2753). One take = one dictation
  // session, from mic-intent to exactly one terminal event — Completed,
  // Cancelled, or Failed. Started fires once per take (at mic-intent, so
  // permission-denied takes still enter the funnel); Completed fires when
  // the take delivers its transcript into review (confirm, confirm
  // grace-timeout, or character-limit auto-stop); Cancelled fires when the
  // take ends without delivering a transcript (✕ button, the 10s silence
  // auto-stop, or leaving the surface mid-recording);
  // Failed carries the reason; Reconnecting fires once per reconnecting
  // episode, not per underlying socket retry attempt. Transcript Edited
  // fires at most once per take, on the first user edit of the reviewed
  // transcript before sending (quality proxy — presence is the signal).
  // Payloads never include the transcript text — only its size.
  educatorDictationStarted: {
    eventName: `${BRAND_NAME} Dictation Started`,
    schema: commonSchema,
    options: { integrations: NON_GOLD_STANDARD_INTEGRATIONS },
  },
  educatorDictationCompleted: {
    eventName: `${BRAND_NAME} Dictation Completed`,
    schema: commonSchema.and(
      z.object({
        duration_ms: z.number(),
        transcript_length: z.number(),
        transcript_words: z.number(),
        // Confirm click → final transcript landing in the input. Omitted
        // when review was reached without a confirm (character-limit
        // auto-stop).
        finalize_latency_ms: z.number().optional(),
      }),
    ),
    options: { integrations: NON_GOLD_STANDARD_INTEGRATIONS },
  },
  educatorDictationCancelled: {
    eventName: `${BRAND_NAME} Dictation Cancelled`,
    schema: commonSchema.and(
      z.object({
        // Time from mic-intent to the abort.
        duration_ms: z.number(),
        cancel_method: dictationCancelMethodSchema,
      }),
    ),
    options: { integrations: NON_GOLD_STANDARD_INTEGRATIONS },
  },
  educatorDictationTranscriptEdited: {
    eventName: `${BRAND_NAME} Dictation Transcript Edited`,
    schema: commonSchema,
    options: { integrations: NON_GOLD_STANDARD_INTEGRATIONS },
  },
  educatorDictationFailed: {
    eventName: `${BRAND_NAME} Dictation Failed`,
    schema: commonSchema.and(
      z.object({
        error_reason: dictationErrorReasonSchema,
      }),
    ),
    options: { integrations: NON_GOLD_STANDARD_INTEGRATIONS },
  },
  educatorDictationReconnecting: {
    eventName: `${BRAND_NAME} Dictation Reconnecting`,
    schema: commonSchema,
    options: { integrations: NON_GOLD_STANDARD_INTEGRATIONS },
  },
  educatorAcceptanceAnimation: {
    eventName: 'Educator Acceptance Animation',
    schema: commonSchema.and(
      z.object({
        action: z.enum(['played', 'completed']),
      }),
    ),
    options: { integrations: NON_GOLD_STANDARD_INTEGRATIONS },
  },
  educatorViewed: {
    eventName: `${BRAND_NAME} Viewed`,
    schema: commonSchema,
    options: { integrations: NON_GOLD_STANDARD_INTEGRATIONS },
  },
  educatorChatClick: {
    eventName: `${BRAND_NAME} Click`,
    schema: commonSchema.and(
      z.object({
        action: z.string(),
        row_name: z.string().optional(),
      }),
    ),
    options: { integrations: NON_GOLD_STANDARD_INTEGRATIONS },
  },
  // Fires when the user clicks the "Get Started" CTA on the one-time cohort
  // welcome intro shown on first login. Spec: JAR-2089.
  educatorWelcomeClick: {
    eventName: `${BRAND_NAME} Click`,
    schema: commonSchema.and(
      z.object({
        action: z.string(),
      }),
    ),
    options: { integrations: NON_GOLD_STANDARD_INTEGRATIONS },
  },
  educatorModalityInteraction: {
    eventName: `${BRAND_NAME} Modality Interaction`,
    schema: commonSchema.and(modalityInteractionSchema),
    options: { integrations: NON_GOLD_STANDARD_INTEGRATIONS },
  },
  // Inline-UI placement (JAR-3636): a `::ui` directive in a live bot message
  // either mounted its modality in the reading flow, or named something the
  // client could not mount. Together they are the production signal for
  // cortex-versus-client registry drift.
  educatorInlineUiMounted: {
    eventName: `${BRAND_NAME} Inline UI Mounted`,
    schema: commonSchema.and(inlineUiMountedSchema),
    options: { integrations: NON_GOLD_STANDARD_INTEGRATIONS },
  },
  educatorInlineUiUnmountable: {
    eventName: `${BRAND_NAME} Inline UI Unmountable`,
    schema: commonSchema.and(
      inlineUiPlacementSchema.extend({
        reason: inlineUiUnmountableReasonSchema,
      }),
    ),
    options: { integrations: NON_GOLD_STANDARD_INTEGRATIONS },
  },
  educatorEmailCaptured: {
    eventName: `${BRAND_NAME} Email Captured`,
    schema: commonSchema.and(
      z.object({
        email: z.string().email(),
        location: z.enum([
          'email form',
          'signup form',
          'program guide modal',
          'stay informed',
        ]),
        action: z.string().optional(),
        marketing_consent: z.boolean().optional(),
      }),
    ),
    options: { integrations: NON_GOLD_STANDARD_INTEGRATIONS },
  },
  educatorModalViewed: {
    eventName: `${BRAND_NAME} Modal Viewed`,
    schema: commonSchema.and(
      z.discriminatedUnion('modal_type', [
        z.object({
          modal_type: z.literal('program guide modal'),
        }),
        z.object({
          modal_type: z.literal('profile modal'),
          tab: profileModalTabSchema,
          payment_status: paymentStatusSchema.optional(),
          certificate_status: certificateStatusSchema.optional(),
        }),
        z.object({
          modal_type: z.literal('certificate preview lightbox'),
          certificate_status: certificateStatusSchema.optional(),
        }),
        z.object({
          modal_type: z.literal('certificate confirmation modal'),
          certificate_status: certificateStatusSchema.optional(),
        }),
        z.object({
          modal_type: z.literal('program completion modal'),
          certificate_status: z.literal('review'),
        }),
        z.object({
          modal_type: z.literal('zoom scheduler modal'),
        }),
        resourceViewerModalProps,
      ]),
    ),
    options: { integrations: NON_GOLD_STANDARD_INTEGRATIONS },
  },
  educatorModalClick: {
    eventName: `${BRAND_NAME} Modal Click`,
    schema: commonSchema.and(
      z.discriminatedUnion('modal_type', [
        z.object({
          modal_type: z.literal('program guide modal'),
          action: z.string(),
        }),
        z.object({
          modal_type: z.literal('profile modal'),
          action: z.string(),
          tab: profileModalTabSchema,
          payment_status: paymentStatusSchema.optional(),
          certificate_status: certificateStatusSchema.optional(),
        }),
        z.object({
          modal_type: z.literal('certificate preview lightbox'),
          action: z.string(),
          certificate_status: certificateStatusSchema.optional(),
        }),
        z.object({
          modal_type: z.literal('certificate confirmation modal'),
          action: z.string(),
        }),
        z.object({
          modal_type: z.literal('program completion modal'),
          action: z.string(),
        }),
        z.object({
          modal_type: z.literal('zoom scheduler modal'),
          action: z.string(),
        }),
        resourceViewerModalProps.extend({ action: z.string() }),
        writtenSubmissionModalProps.extend({
          action: z.literal('close'),
          close_method: writtenSubmissionCloseMethodSchema,
          essay_word_count: z.number().int().nonnegative(),
          essay_focused: z.boolean(),
          linkedin_focused: z.boolean(),
          linkedin_filled: z.boolean(),
        }),
      ]),
    ),
    options: { integrations: NON_GOLD_STANDARD_INTEGRATIONS },
  },
  educatorFormFieldInteracted: {
    eventName: `${BRAND_NAME} Form Field Interacted`,
    schema: commonSchema.and(
      z.discriminatedUnion('modal_type', [
        writtenSubmissionModalProps.extend({
          field: writtenSubmissionFieldSchema,
        }),
      ]),
    ),
    options: { integrations: NON_GOLD_STANDARD_INTEGRATIONS },
  },
  educatorFormFieldTyped: {
    eventName: `${BRAND_NAME} Form Field Typed`,
    schema: commonSchema.and(
      z.discriminatedUnion('modal_type', [
        writtenSubmissionModalProps.extend({
          field: writtenSubmissionFieldSchema,
        }),
      ]),
    ),
    options: { integrations: NON_GOLD_STANDARD_INTEGRATIONS },
  },
  educatorFormFieldErrorViewed: {
    eventName: `${BRAND_NAME} Form Field Error Viewed`,
    schema: commonSchema.and(
      z.discriminatedUnion('modal_type', [
        writtenSubmissionModalProps.extend({
          field: writtenSubmissionFieldSchema,
          error_type: writtenSubmissionFieldErrorTypeSchema,
        }),
      ]),
    ),
    options: { integrations: NON_GOLD_STANDARD_INTEGRATIONS },
  },
  // HDYHAU: same event names and payload shape as core/certs.
  educatorReferralSourcesCompleted: {
    eventName: 'Referral Sources Completed',
    schema: z.object({
      ...goldStandardSchema.shape,
      location: z.literal('written submission'),
      application_id: z.string(),
      referral_sources: z.array(z.string()).min(1),
    }),
    options: { integrations: NON_GOLD_STANDARD_INTEGRATIONS },
  },
  educatorAudioReferralSourcesCompleted: {
    eventName: 'Audio Referral Sources Completed',
    schema: z.object({
      ...goldStandardSchema.shape,
      location: z.literal('written submission'),
      application_id: z.string(),
      referral_sources: z.array(z.string()).min(1),
      audio_referral_sources: z.array(z.string().min(1)).min(1),
    }),
    options: { integrations: NON_GOLD_STANDARD_INTEGRATIONS },
  },
  educatorUserInfoCaptured: {
    eventName: `${BRAND_NAME} User Info Captured`,
    schema: commonSchema.and(
      z.object({
        email: z.string().email(),
        location: z.literal('user info form'),
        professional_goals: z.array(z.string()),
        goal_other: z.string().optional(),
        years_experience: z.string(),
        current_role: z.string(),
        industry: z.string(),
        learning_timeline: z.string(),
      }),
    ),
    options: { integrations: NON_GOLD_STANDARD_INTEGRATIONS },
  },
  // Home Landing & Subject Page events
  // Spec: https://masterclass-dev.atlassian.net/wiki/spaces/ENG/pages/3585802258
  educatorRowTileViewed: {
    eventName: `${BRAND_NAME} Row Tile Viewed`,
    schema: commonSchema
      .and(
        z.object({
          row_name: z.string(),
          row_position_index: z.number(),
          tile_position_index: z.number(),
          tile_title: z.string().optional(),
          tile_subtitle: z.string().optional(),
          tile_link: z.string().optional(),
          subject_slug: z.string().optional(),
          module_slug: z.string().optional(),
          topic_slug: z.string().optional(),
          event_slug: z.string().optional(),
          view_type: modulesViewTypeSchema.optional(),
        }),
      )
      .and(resourceEventProps)
      .superRefine(refineViewTypeScope),
    options: { integrations: NON_GOLD_STANDARD_INTEGRATIONS },
  },
  educatorHubClick: {
    eventName: `${BRAND_NAME} Click`,
    schema: commonSchema
      .and(
        z.object({
          action: z.string(),
          location: z.string().optional(),
          row_name: z.string().optional(),
          row_position_index: z.number().optional(),
          tile_position_index: z.number().optional(),
          tile_title: z.string().optional(),
          tile_subtitle: z.string().optional(),
          tile_link: z.string().optional(),
          subject_slug: z.string().optional(),
          module_slug: z.string().optional(),
          topic_slug: z.string().optional(),
          event_slug: z.string().optional(),
          view_type: modulesViewTypeSchema.optional(),
        }),
      )
      .and(resourceEventProps)
      .superRefine(refineViewTypeScope),
    options: { integrations: NON_GOLD_STANDARD_INTEGRATIONS },
  },
  educatorAiLabsSubmissionStarted: {
    eventName: `${BRAND_NAME} AI Labs Submission Started`,
    schema: commonSchema.and(
      z.object({
        upload_method: uploadMethodSchema,
        // Coerced: arrives as a string via the backend submission `analytics`
        // map (additionalProperties: string); optional until BE populates it.
        attempt_number: z.coerce.number().optional(),
      }),
    ),
    options: { integrations: NON_GOLD_STANDARD_INTEGRATIONS },
  },
  educatorAiLabsSubmissionCompleted: {
    eventName: `${BRAND_NAME} AI Labs Submission Completed`,
    schema: commonSchema.and(
      z.object({
        attempt_number: z.coerce.number().optional(),
        share_consent: z.boolean().optional(),
      }),
    ),
    options: { integrations: NON_GOLD_STANDARD_INTEGRATIONS },
  },
  educatorAiLabsSubmissionFailed: {
    eventName: `${BRAND_NAME} AI Labs Submission Failed`,
    schema: commonSchema.and(
      z.object({
        error_type: z.enum(['file_too_large', 'invalid_format']),
        upload_method: uploadMethodSchema,
      }),
    ),
    options: { integrations: NON_GOLD_STANDARD_INTEGRATIONS },
  },

  // Payment Failure - fires on payment error (any error shown to the user on the payment step)
  educatorPaymentFailure: {
    eventName: 'Payment Failure',
    schema: commonSchema.and(
      z.object({
        email: z.string().email(),
        error_data_code: z.string().optional(),
        error_data_message: z.string(),
        error_type: z.literal('Purchase failed').default('Purchase failed'),
        payment_provider: z.string().optional(),
        mc_geo: z.string(),
        declineCode: z.string().optional(),
        ...paymentFailureCommerceSchema.shape,
      }),
    ),
    options: { integrations: NON_GOLD_STANDARD_INTEGRATIONS },
  },

  // ========================================
  // IN-PERSON EVENT CHECKOUT (JAR-4283)
  // ========================================
  // The event names are the shared canonical ones, so the seat lands in the
  // same funnel queries as the program fee; `purchase_source` is what tells
  // them apart. The entries are still separate from the program checkout's:
  //   - they are analytics-only. The seat is sold to members who are already
  //     enrolled, so forwarding it to paid media would train acquisition
  //     campaigns on an audience that cannot be acquired, and inflate the
  //     conversion value those campaigns optimise on.
  //   - there is no `payment_plan_type` here, and the program's entries
  //     require one.

  // Fires when the checkout screen opens on its buyable state.
  boothInitiateCheckout: {
    eventName: 'Initiate Checkout',
    schema: commonSchema.and(
      z.object({
        ...inPersonCommerceShape,
        quantity: z.number().default(1),
        contents: contentsSchema.optional(),
      }),
    ),
    options: { integrations: NON_GOLD_STANDARD_INTEGRATIONS },
  },

  // Fires when the card-entry modal opens — the member has no saved card, or
  // chose to use a different one. Bound to the form rather than to the screen
  // opening, which would make it a duplicate of Initiate Checkout.
  boothPaymentInfoStepViewed: {
    eventName: 'Payment Info Step Viewed',
    schema: commonSchema.and(
      z.object({
        ...inPersonCommerceShape,
        products: productsSchema,
        revenue: z.number(),
      }),
    ),
    options: { integrations: NON_GOLD_STANDARD_INTEGRATIONS },
  },

  // Fires when "Confirm and pay" is pressed, before the card is validated, so
  // that pressed-pay partitions cleanly into Order Completed | Payment Failure.
  boothProductAdded: {
    eventName: 'Product Added',
    schema: commonSchema.and(
      z.object({
        ...inPersonCommerceShape,
        quantity: z.number().default(1),
        payment_method: inPersonPaymentMethodSchema,
      }),
    ),
    options: { integrations: NON_GOLD_STANDARD_INTEGRATIONS },
  },

  // Fires when the bank hands back a 3D Secure challenge, before the iframe
  // opens. Without it a challenge that never completes is indistinguishable
  // from an abandonment: Product Added, then silence.
  boothPaymentChallengeStarted: {
    eventName: 'Payment Intent Required Action Started',
    schema: commonSchema.and(
      z.object({
        ...inPersonCommerceShape,
        payment_intent_action: z
          .literal('3ds_authentication')
          .default('3ds_authentication'),
        payment_provider: z.string(),
        order_id: z.string(),
      }),
    ),
    options: { integrations: NON_GOLD_STANDARD_INTEGRATIONS },
  },

  // Fires once the seat is paid for, on both the direct and the post-3DS path.
  // The card and order descriptors are optional because they come from the
  // order's `analytics` map, an untyped string passthrough on the contract —
  // a missing one should not invalidate the revenue.
  boothOrderCompleted: {
    eventName: 'Order Completed',
    schema: commonSchema.and(
      z.object({
        ...inPersonCommerceShape,
        content_ids: z.array(z.number()),
        products: orderCompletedProductsSchema,
        quantity: z.number().default(1),
        revenue: z.number(),
        revenue_in_usd: z.number().optional(),
        total: z.number(),
        purchase_amount: z.number(),
        purchase_currency: z.string().default('USD'),
        // Ambient, from the app-wide TrackingAttributes. Optional so a session
        // without one cannot invalidate the revenue.
        em: z.string().email().optional(),
        order_id: z.string().optional(),
        first_time_order: z.boolean().optional(),
        payment_provider: z.string().optional(),
        product_sku: z.string().optional(),
        productId: z.string().optional(),
        paymentId: z.string().optional(),
        card_brand: z.string().optional(),
        card_last_4: z.string().optional(),
        payment_method: inPersonPaymentMethodSchema.optional(),
      }),
    ),
    options: { integrations: NON_GOLD_STANDARD_INTEGRATIONS },
  },

  // ========================================
  // GOLD STANDARD EVENTS (Sent to Paid Media)
  // ========================================
  // These events are sent to Meta, Google Ads, and GTM for optimization
  // Spec: https://masterclass-dev.atlassian.net/wiki/spaces/MAR/pages/3506896897

  // Cart Email Captured - fires when registration modal/page initiated (once per email)
  educatorCartEmailCaptured: {
    eventName: 'Cart Email Captured',
    schema: z.object({
      ...goldStandardSchema.shape,
      page: z.enum([
        educatorPageNames.EMAIL_ENTRY,
        educatorPageNames.REGISTER,
        educatorPageNames.ENROLLMENT_OVERVIEW,
      ]),
      email: z.string().email(),
      location: z.string(),
      marketing_consent: z.boolean().optional(),
      email_capture_method: z.string().optional(),
      gdpr_opt_in: z.boolean().optional(),
    }),
    options: { integrations: GOLD_STANDARD_INTEGRATIONS },
  },

  // Pre-cart Email Captured - fires when user submits their email on EmailEntryPage
  // consent: true for non-GDPR users, not fired for GDPR users
  educatorPreCartEmailCaptured: {
    eventName: 'Pre-cart Email Captured',
    schema: z.object({
      ...goldStandardSchema.shape,
      page: z.enum([
        educatorPageNames.EMAIL_ENTRY,
        educatorPageNames.REGISTER,
        educatorPageNames.ENROLLMENT_OVERVIEW,
      ]),
      email: z.string().email(),
      location: z.string(),
      marketing_consent: z.boolean().optional(),
      email_capture_method: z.string().optional(),
      gdpr_opt_in: z.boolean().optional(),
    }),
    options: { integrations: LCM_INTEGRATIONS },
  },

  educatorPurchaseClick: {
    eventName: 'Purchase Click',
    schema: commonSchema.and(
      z.object({
        ...goldStandardSchema.shape,
        cta_location: z.enum([
          'hero',
          'signup form',
          'sticky footer',
          'sticky nav',
          'top navigation',
          'enrollment block',
          'ai labs',
          'cta band',
        ]),
        cta: z.string(),
        // Commerce fields (optional so the event still fires without pricing;
        // quantity/currency are only set when real product data is present).
        product_id: z.number().optional(),
        name: z.literal('educator').default('educator'),
        content_type: z.literal('product').default('product'),
        quantity: z.number().optional(),
        price: z.number().optional(),
        currency: z.string().optional(),
        contents: contentsSchema.optional(),
      }),
    ),
    options: { integrations: GOLD_STANDARD_INTEGRATIONS },
  },
  educatorProductViewed: {
    eventName: 'Product Viewed',
    schema: commonSchema.and(
      z.object({
        ...goldStandardSchema.shape,
        content_type: z.array(z.string()).default(['product']),
        content_ids: z.array(z.string()).default(['educator']),
        content_name: z.array(z.string()).default(['Educator']),
      }),
    ),
    options: { integrations: GOLD_STANDARD_INTEGRATIONS },
  },
  educatorApplicationStarted: {
    eventName: 'Application Started',
    schema: z.object({
      ...goldStandardSchema.shape,
      em: z.string().email().optional(),
      email: z.string().email().optional(),
    }),
    options: { integrations: GOLD_STANDARD_INTEGRATIONS },
  },
  educatorApplicationSubmitted: {
    eventName: 'Application Submitted',
    schema: z.object({
      ...goldStandardSchema.shape,
      ...enrollmentApplicationGoldStandardSchema.shape,
      registration_method: registrationMethodSchema.optional(),
      ...leadValueShape,
    }),
    options: { integrations: GOLD_STANDARD_INTEGRATIONS },
  },
  educatorChallengeCompleted: {
    eventName: 'Challenge Completed',
    schema: z.object({
      ...goldStandardSchema.shape,
      challenge_type: z.literal('written_assessment'),
      application_id: z.string(),
      em: z.string().email().optional(),
      ...leadValueShape,
    }),
    options: { integrations: GOLD_STANDARD_INTEGRATIONS },
  },
  educatorApplicationQualified: {
    eventName: 'Application Qualified',
    schema: z.object({
      ...goldStandardSchema.shape,
      ...enrollmentApplicationGoldStandardSchema.shape,
      registration_method: registrationMethodSchema.optional(),
      ...leadValueShape,
    }),
    options: { integrations: GOLD_STANDARD_INTEGRATIONS },
  },
  educatorApplicationDisqualified: {
    eventName: 'Application Disqualified',
    schema: z.object({
      ...goldStandardSchema.shape,
      ...enrollmentApplicationGoldStandardSchema.shape,
      registration_method: registrationMethodSchema.optional(),
    }),
    options: { integrations: GOLD_STANDARD_INTEGRATIONS },
  },
  educatorRegistrationCompleted: {
    eventName: 'Registration Completed',
    schema: z.object({
      ...goldStandardSchema.shape,
      auth_method: registrationMethodSchema,
      is_new_user: z.boolean(),
      first_name: z.string().optional(),
      last_name: z.string().optional(),
      em: z.string().optional(),
      email: z.string().optional(),
      registered_at: z.enum(['pre application', 'post application']).optional(),
    }),
    options: { integrations: GOLD_STANDARD_INTEGRATIONS },
  },

  // Initiate Checkout - fires when user clicks "Secure Your Spot" on accepted status page
  educatorInitiateCheckout: {
    eventName: 'Initiate Checkout',
    schema: z.object({
      ...goldStandardSchema.shape,
      content_type: z.literal('product').default('product'),
      // Commerce fields flow in ambiently from purchaseAnalytics (spread into
      // TrackingAttributes in _app); declared optional so they survive safeParse.
      product_id: z.number().optional(),
      quantity: z.number().optional(),
      price: z.number().optional(),
      currency: z.string().optional(),
      contents: contentsSchema.optional(),
      // Computed client-side on the accepted status page CTA.
      timezone: z.string().optional(),
      user_agentData: z.record(z.unknown()).optional(),
    }),
    options: { integrations: GOLD_STANDARD_INTEGRATIONS },
  },

  // Product Added - fires when user selects a payment plan and submits
  educatorProductAdded: {
    eventName: 'Product Added',
    schema: z.object({
      ...goldStandardSchema.shape,
      product_id: z.number(),
      name: z.literal('educator').default('educator'),
      content_type: z.literal('product').default('product'),
      quantity: z.number().default(1),
      price: z.number(),
      currency: z.string().default('USD'),
      payment_plan_type: paymentPlanTypeSchema,
    }),
    options: { integrations: GOLD_STANDARD_INTEGRATIONS },
  },

  // Payment Info Step Viewed - fires when payment method page loads
  educatorPaymentInfoStepViewed: {
    eventName: 'Payment Info Step Viewed',
    schema: z.object({
      ...goldStandardSchema.shape,
      content_type: z.literal('product').default('product'),
      products: productsSchema,
      revenue: z.number(),
      currency: z.string().default('USD'),
      payment_plan_type: paymentPlanTypeSchema,
    }),
    options: { integrations: GOLD_STANDARD_INTEGRATIONS },
  },

  // Order Completed - fires on successful payment
  educatorOrderCompleted: {
    eventName: 'Order Completed',
    schema: z.object({
      ...goldStandardSchema.shape,
      payment_plan_type: paymentPlanTypeSchema,
      product_id: z.number(),
      products: orderCompletedProductsSchema,
      content_ids: z.array(z.number()),
      content_type: z.literal('product').default('product'),
      currency: z.string().default('USD'),
      purchase_currency: z.string().default('USD'),
      revenue: z.number(),
      revenue_in_usd: z.number(),
      total: z.number(),
      purchase_amount: z.number(),
      em: z.string().email(),
      first_time_order: z.boolean(),
      payment_provider: z.string(),
      quantity: z.number().default(1),
      product_sku: z.string(),
      productId: z.string(),
      paymentId: z.string(),
      order_id: z.string(),
      card_brand: z.string(),
      card_last_4: z.string(),
    }),
    options: { integrations: GOLD_STANDARD_INTEGRATIONS },
  },
} as const

// Exported for tests: this is the schema TrackPageViewed ('Page Loaded')
// validates against, and unlike other events it drops the event entirely on
// failure — regressions here silently lose page views.
export const pageSchema = commonSchema.and(
  z.object({
    product: z.literal(ANALYTICS_PRODUCT_NAME).default(ANALYTICS_PRODUCT_NAME),
    product_name: z
      .literal(ANALYTICS_PRODUCT_NAME)
      .default(ANALYTICS_PRODUCT_NAME),
    name: pageSource.or(z.literal(educatorPageNames.REFERRAL_SOURCES)),
  }),
)

const {
  useFireEvent,
  TrackExperimentViewed,
  TrackEvent,
  TrackClick,
  TrackPageViewed,
  TrackOnMount,
  TrackVisible,
  TrackingAttributes,
} = createTracking(educatorSchema, pageSchema)

export const nameToTrackingAction = (name: string) =>
  name.toLowerCase().replace(/\s+/g, '_')

export {
  useFireEvent,
  TrackExperimentViewed,
  TrackEvent,
  TrackClick,
  TrackPageViewed,
  TrackOnMount,
  TrackVisible,
  TrackingAttributes,
}

export type { TrackableChildProps } from '@mc/tracking-kit'

export type EducatorTrackingLocation = z.infer<typeof locations>
export type ActivityType = z.infer<typeof modalityTypeSchema>
export type ModalityAction = z.infer<typeof modalityActionSchema>
export type ModalityInteractionEvent = z.infer<typeof modalityInteractionSchema>
export type InlineUiPlacementEvent = z.infer<typeof inlineUiPlacementSchema>
export type InlineUiMountedEvent = z.infer<typeof inlineUiMountedSchema>
export type InlineUiUnmountableReason = z.infer<
  typeof inlineUiUnmountableReasonSchema
>

/**
 * Helper to fire educatorCartEmailCaptured event and also send to backend via GrowthService
 * This ensures the event is tracked both in Segment (via useFireEvent) and Iterable (via backend API)
 */
export const fireCartEmailCaptured = async (
  fireEvent: ReturnType<typeof useFireEvent>,
  properties: Parameters<ReturnType<typeof useFireEvent>>[1] & {
    email: string
  },
) => {
  // Generate a unique event ID for deduplication
  const eventId = crypto.randomUUID()

  // Fire the Segment event via tracking-kit
  fireEvent('educatorCartEmailCaptured', properties)
  // Send essential data to backend for Iterable tracking
  const emailCaptureRequest: EmailCaptureRequest = {
    email: properties.email,
    eventName: 'Cart Email Captured',
    campaign: undefined,
    marketingConsent: properties.marketing_consent ?? true,
    product: 'educator',
    eventId,
  }

  // Track on backend via Iterable (fail silently if it errors)
  try {
    await GrowthService.createEmailCapture({
      requestBody: emailCaptureRequest,
    })
  } catch {
    // Fail silently - Segment tracking above already fired
  }
}
export type WrittenSubmissionField = z.infer<
  typeof writtenSubmissionFieldSchema
>
export type WrittenSubmissionFieldErrorType = z.infer<
  typeof writtenSubmissionFieldErrorTypeSchema
>
export type WrittenSubmissionCloseMethod = z.infer<
  typeof writtenSubmissionCloseMethodSchema
>
